declare const __APP_VERSION__: string | undefined;
/** The build's version (vite define: package version + commit), "dev" outside a build. */
export const APP_VERSION = typeof __APP_VERSION__ === "string" && __APP_VERSION__ ? __APP_VERSION__ : "dev";

/** The static file the build writes next to index.html (vite.config.ts), never cached (public/_headers). */
export const VERSION_FILE = "version.json";
export const VERSION_CHECK_INTERVAL_MS = 10 * 60_000;

export interface VersionWatchOptions {
  /** The version of the JavaScript running in this tab. */
  current: string;
  fetch?: typeof fetch;
  /** Where version.json is; relative so it also works under a sub path. */
  url?: string;
  intervalMs?: number;
  /** The window and document to listen on (focus, visibilitychange). */
  target?: Pick<Window, "addEventListener" | "removeEventListener" | "setInterval" | "clearInterval">;
  doc?: Pick<Document, "addEventListener" | "removeEventListener" | "visibilityState">;
  /** Called once, with the served version, when it differs from the running one. */
  onNewVersion: (served: string) => void;
}

/** The served version, or null when it cannot be read (offline, old build without the file, bad content). */
export async function fetchServedVersion(fetcher: typeof fetch, url = VERSION_FILE): Promise<string | null> {
  try {
    const res = await fetcher(url, { cache: "no-store" });
    if (!res.ok) return null;
    const body = (await res.json()) as { version?: unknown };
    return typeof body.version === "string" && body.version.trim() ? body.version.trim() : null;
  } catch {
    return null;
  }
}

/** A newer build is out when the served version is known and differs from the running one. */
export const isNewVersion = (current: string, served: string | null): served is string => served !== null && served !== current;

/**
 * Checks version.json when the tab becomes visible again, when the window gets the focus and every
 * intervalMs; reports a different version once and then stops. Returns the stop function.
 */
export function startVersionWatch(o: VersionWatchOptions): () => void {
  const fetcher = o.fetch ?? globalThis.fetch.bind(globalThis);
  const target = o.target ?? globalThis.window;
  const doc = o.doc ?? globalThis.document;
  let stopped = false;
  let busy = false;
  const check = async () => {
    if (stopped || busy) return;
    busy = true;
    try {
      const served = await fetchServedVersion(fetcher, o.url);
      if (!stopped && isNewVersion(o.current, served)) {
        stop();
        o.onNewVersion(served);
      }
    } finally {
      busy = false;
    }
  };
  const onVisible = () => {
    if (doc.visibilityState === "visible") void check();
  };
  const onFocus = () => void check();
  doc.addEventListener("visibilitychange", onVisible);
  target.addEventListener("focus", onFocus);
  const timer = target.setInterval(() => void check(), o.intervalMs ?? VERSION_CHECK_INTERVAL_MS);
  function stop() {
    if (stopped) return;
    stopped = true;
    doc.removeEventListener("visibilitychange", onVisible);
    target.removeEventListener("focus", onFocus);
    target.clearInterval(timer);
  }
  return stop;
}

/**
 * Whether this build watches for new versions: only a real production build. Never in dev or tests,
 * and not in the browser test demo build (VITE_DEMO_PERSIST) unless a test sets
 * window.__garagevoVersionCheck = true before the app starts.
 */
export function versionCheckEnabled(env: Record<string, unknown> = import.meta.env, g: object = globalThis): boolean {
  if (!env.PROD || env.MODE === "test") return false;
  if (env.VITE_DEMO_PERSIST === "true") return (g as { __garagevoVersionCheck?: unknown }).__garagevoVersionCheck === true;
  return true;
}
